import { getIdToken } from "@/services/Auth";
import { API_ROUTES } from "@/constants";

import { getBaseApiUrl, getDefaultHeaders, request } from "./index";
import { resourceManagerRequest, RESOURCE_MANAGER_TYPES } from "./resourceManager";

/**
 * CREATE PLAINLY PROJECT
 */

interface BaseLayer {
    duration: number;
    internalId: string;
    guideLayer: boolean;
    workAreaStart: number;
    name: string;
    width: number;
    id: number;
    enabled: boolean;
    height: number;
}

interface ChildBaseLayerProps {
    outPoint: number;
    inPoint: number;
    startTime: number;
}

type ChildBaseLayer = BaseLayer & ChildBaseLayerProps;

interface CompositionLayerProps {
    type: "COMPOSITION";
    children: ChildLayer[] | [];
}

export type CompositionLayer = BaseLayer & CompositionLayerProps;

interface ChildCompositionLayerProps {
    type: "COMPOSITION";
    layerName: string;
    children: ChildLayer[] | [];
}

type ChildCompositionLayer = ChildBaseLayer & ChildCompositionLayerProps;

interface ChildMediaLayerProps {
    preview: {
        link: string;
        expiresAt: string;
        internal: boolean;
    };
    mediaType: "IMAGE" | "VIDEO" | "AUDIO";
    type: "MEDIA";
    value: string;
}

type ChildMediaLayer = ChildBaseLayer & ChildMediaLayerProps;

interface ChildTextLayerProps {
    fontLocation: string;
    type: "TEXT";
    fontStyle: string;
    fontFamily: string;
    fontSize: number;
    value: string;
    font: string;
}

type ChildTextLayer = ChildBaseLayer & ChildTextLayerProps;

interface ChildSolidLayerProps {
    mediaType: "SOLID";
    type: "MEDIA";
    value: string;
}

type ChildSolidLayer = ChildBaseLayer & ChildSolidLayerProps;

interface HackComposedLayerType {
    children?: ChildLayer[];
    mediaType?: "IMAGE" | "VIDEO" | "AUDIO" | "SOLID";
    value?: string;
}

export type ChildLayer = (ChildMediaLayer | ChildCompositionLayer | ChildTextLayer | ChildSolidLayer) &
    HackComposedLayerType;

export async function createPlainlyProject({ id, bucket, key }: { id: string; bucket: string; key: string }) {
    return resourceManagerRequest<{
        plainlyProjectId: string;
        layersFileLocation: { signedUrl: string };
    }>({
        id,
        body: {
            type: RESOURCE_MANAGER_TYPES.PLAINLY_CREATE_PROJECT,
            input: {
                storageInfo: {
                    bucket,
                    key
                }
            }
        }
    });
}

/**
 * CREATE PLAINLY RENDER
 */

export type PlainlyLayerManagement =
    | {
          font: string;
          fontSize?: string;
      }
    | {
          font?: string;
          fontSize: string;
      };

interface PlainlyComputedTextLayer {
    name: string;
    value: string;
    layerManagement?: PlainlyLayerManagement;
}

interface PlainlyComputedMediaLayer {
    name: string;
    fileLocation: {
        bucket: string;
        key: string;
    };
}

export type PlainlyComputedLayer = PlainlyComputedTextLayer | PlainlyComputedMediaLayer;

interface RequestPlainlyRenderInput {
    reportingLabel: string;
    plainlyProjectId: string;
    plainlyTemplateId: string;
    outputOptions: {
        preview: boolean;
    };
    computedLayers: PlainlyComputedLayer[];
}

export function createPlainlyRender({ id, input }: { id: string; input: RequestPlainlyRenderInput }) {
    return resourceManagerRequest<{
        fileLocation: {
            bucket: string;
            key: string;
            signedUrl: string;
        };
    }>({
        id,
        body: {
            type: RESOURCE_MANAGER_TYPES.PLAINLY_RENDER,
            input
        }
    });
}

/**
 * CREATE PLAINLY BATCH RENDER
 */
export function createPlainlyBatchRender({ id, inputs }: { id: string; inputs: RequestPlainlyRenderInput[] }) {
    return resourceManagerRequest<{
        zipFileLocation: {
            bucket: string;
            key: string;
            signedUrl: string;
        };
    }>({
        id,
        body: {
            type: RESOURCE_MANAGER_TYPES.PLAINLY_BATCH_RENDER,
            input: {
                recipients: [],
                itemsToRender: inputs
            }
        }
    });
}

/**
 * CREATE PLAINLY TEMPLATES
 */
export async function createPlainlyTemplates({
    resourceId,
    templates
}: {
    resourceId: string;
    templates: {
        name: string;
        renderingCompositionId: number;
        layers: {
            id: string;
            label?: string;
        }[];
    }[];
}) {
    const authorizationHeader = await getIdToken();
    const headers = getDefaultHeaders(authorizationHeader);
    const baseUrl = getBaseApiUrl();

    const url = `${baseUrl}${API_ROUTES.PLAINLY_ADD_TEMPLATES}`;

    return request<string[]>(url, headers, "POST", { resourceId, templates });
}
